import { MagnifyingGlassIcon, XMarkIcon } from '@heroicons/react/24/outline';
import React, { useCallback, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import FilterDropdown from '../components/filtering/FilterDropdown';
import { practiceOptionsFromList } from '../utils/practiceFilterOptions';
import {
    useServerPatients,
    useInfinitePatients,
    usePatientFilterOptions
} from '../hooks/useServerPatients';
import { usePracticeOptions } from '../hooks/useBilling';
import { usePageTitle } from '../hooks/usePageTitle';
import { DATA_TABLE_ROW_HEIGHT } from '../hooks/useViewportPageSize';
import { useViewportTableLimit } from '../hooks/useViewportTableLimit';
import { useDebounce } from '@odo/shared-ui';
import { Avatar } from '../components/Avatar';
import { StatusPill } from '../components/patient-files/overview/shared';
import { VersionBadge } from '../components/shared/VersionBadge';
import { PatientPracticeCell } from '../components/shared/PatientPracticeCell';
import { useStatusCatalog } from '../services/statusCatalog';
import { useMeta } from '../hooks/useWorkQueue';
import { phaseColorOverride } from '../utils/phaseColors';
import {
    DataTable,
    useDataTable,
    MoreFiltersMenu,
    type DataTableColumn,
    type MoreMenuCategory
} from '@odo/shared-ui';
import type { PatientRow } from '../services/patientsV2';

const DEFAULT_PAGE_SIZE = 10;

type SortColumn = 'patient' | 'practice';

// Type alias, not interface: useDataTable's filter-map constraint requires it.
type PatientFilters = {
    practice: string[];
    caseStatus: string[];
    // Workflow macro ids (StatusMeta.macro) - the Phase filter.
    macro: string[];
    actionRequired: string[];
};

const STATUS_CELL = 'px-6 py-0 overflow-hidden';
const STATUS_HEADER = 'px-6';
// The viewport fit measures from the table's py-4 wrapper, so only its bottom padding stays free.
const PATIENTS_TABLE_BOTTOM_SPACE = 16;

const Patients: React.FC = () => {
    usePageTitle('Patients');
    const navigate = useNavigate();
    const catalog = useStatusCatalog();

    // Search stays outside useDataTable (needs debouncing + its legacy key).
    const [searchTerm, setSearchTermRaw] = useState(() => {
        try {
            return sessionStorage.getItem('odo_fv_search') ?? '';
        } catch {
            return '';
        }
    });
    const debouncedSearch = useDebounce(searchTerm, 300);

    const table = useDataTable<SortColumn, PatientFilters>({
        defaultSort: { by: 'patient', order: 'asc' },
        defaultLimit: DEFAULT_PAGE_SIZE,
        defaultFilters: { practice: [], caseStatus: [], macro: [], actionRequired: [] },
        storageKey: 'odo_fv_patients_table',
        filterStorageKeys: {
            // Values are practice IDS (names aren't unique) - the key differs
            // from the retired name-based one so stale name selections can't
            // be restored as ids.
            practice: 'odo_fv_practice_id_filter',
            caseStatus: 'odo_fv_case_status_filter',
            // New key (not the retired ortho-status one) - old stored values
            // are ortho-status labels, not macro ids, and must not be reused.
            macro: 'odo_fv_phase_macro_filter',
            actionRequired: 'odo_fv_action_required_filter'
        }
    });
    const { setPage } = table;

    const setSearchTerm = useCallback(
        (v: string) => {
            setSearchTermRaw(v);
            // Search is server-side: any change restarts from page 1.
            setPage(1);
            try {
                sessionStorage.setItem('odo_fv_search', v);
            } catch {
                /* best-effort persistence */
            }
        },
        [setPage]
    );

    // Shared server params; the active query is picked by the table mode.
    const isInfinite = table.mode === 'infinite';
    const serverFilters = {
        sort: table.sort,
        order: table.order,
        practiceIds: table.filters.practice.length > 0 ? table.filters.practice : undefined,
        caseStatus: table.filters.caseStatus.length > 0 ? table.filters.caseStatus : undefined,
        macro: table.filters.macro.length > 0 ? table.filters.macro : undefined,
        actionRequired:
            table.filters.actionRequired.length > 0 ? table.filters.actionRequired : undefined,
        search: debouncedSearch || undefined
    };
    const viewport = useViewportTableLimit(table, {
        rowHeight: DATA_TABLE_ROW_HEIGHT,
        bottomSpace: PATIENTS_TABLE_BOTTOM_SPACE
    });
    const paged = useServerPatients({
        ...serverFilters,
        page: table.page,
        limit: table.limit,
        enabled: !isInfinite && viewport.isReady
    });
    const infinite = useInfinitePatients({
        ...serverFilters,
        limit: table.limit,
        enabled: isInfinite
    });

    const patients = isInfinite ? infinite.patients : paged.patients;
    const meta = isInfinite ? infinite.meta : paged.meta;
    const isLoading = isInfinite ? infinite.isLoading : paged.isLoading;
    const isFetching = isInfinite ? infinite.isFetching : paged.isFetching;
    const isError = isInfinite ? infinite.isError : paged.isError;
    // refresh invalidates by the PATIENTS_KEY prefix, so it covers both queries.
    const { refresh } = paged;

    const { hasNextPage, isFetchingNextPage, fetchNextPage } = infinite;
    const handleLoadMore = useCallback(() => {
        if (hasNextPage && !isFetchingNextPage) void fetchNextPage();
    }, [hasNextPage, isFetchingNextPage, fetchNextPage]);

    const { filterOptions } = usePatientFilterOptions();
    const { data: workflowMeta } = useMeta();

    // Id-keyed options from GET /v2/practices/options, doctor as sub-label
    // and keyword - two practices sharing a name stay two distinct options.
    const { practices } = usePracticeOptions();
    const practiceOptions = useMemo(() => practiceOptionsFromList(practices), [practices]);

    // macro -> phase is 1:1 (see WorkflowMacro/MACRO_LABELS), so deduping by
    // macro is enough - ordered by meta.phases so the filter reads in
    // workflow order rather than alphabetically.
    const phaseOptions = useMemo(() => {
        const phaseOrder = workflowMeta?.phases ?? [];
        const byMacro = new Map<string, string>();
        for (const status of workflowMeta?.statuses ?? []) {
            if (!byMacro.has(status.macro)) byMacro.set(status.macro, status.phase);
        }
        return Array.from(byMacro, ([macro, phase]) => ({ value: macro, label: phase })).sort(
            (a, b) => phaseOrder.indexOf(a.label) - phaseOrder.indexOf(b.label)
        );
    }, [workflowMeta]);

    // Secondary status filters live behind the "More" button (mirrors the work queue).
    const moreCategories: MoreMenuCategory[] = [
        {
            key: 'caseStatus',
            label: 'External Status',
            options: filterOptions.caseStatuses,
            selectedOptions: table.filters.caseStatus,
            onChange: (v) => table.setFilter('caseStatus', v)
        },
        {
            key: 'macro',
            label: 'Phase',
            options: phaseOptions,
            selectedOptions: table.filters.macro,
            onChange: (v) => table.setFilter('macro', v)
        }
    ];
    const moreActiveCount = moreCategories.filter((c) => c.selectedOptions.length > 0).length;

    const handleOpenFiles = useCallback(
        (patient: { id: string; name: string }) => {
            navigate(`/patients/${patient.id}`, { state: { patientName: patient.name } });
        },
        [navigate]
    );

    const columns = useMemo<DataTableColumn<PatientRow, SortColumn>[]>(
        () => [
            {
                key: 'patient',
                label: 'PATIENT',
                width: '26%',
                sortable: true,
                sortKey: 'patient',
                cellClassName: 'pl-6 pr-1 py-0 overflow-hidden',
                headerClassName: 'pl-6 pr-3',
                render: (patient) => (
                    <PatientPracticeCell
                        patientName={patient.name}
                        nameClassName="block truncate min-w-0 text-sm font-semibold leading-4 tracking-[0.005em] text-gray-800 underline cursor-pointer hover:text-gray-600 transition-colors"
                        nameTooltipContent={patient.name}
                        onPatientClick={() => handleOpenFiles(patient)}
                        avatar={
                            <Avatar
                                user={{ name: patient.name, email: '' }}
                                thumbnailURL={patient.profileThumbnailUrl ?? undefined}
                            />
                        }
                        badges={
                            <div className="flex-shrink-0">
                                <VersionBadge value={patient.version} />
                            </div>
                        }
                        practiceName={patient.practiceName}
                        doctorName={patient.practiceDoctor}
                        format="doctor-practice"
                        sublineToneClassName="text-gray-500"
                    />
                )
            },
            {
                key: 'phase',
                label: 'PHASE',
                width: '16%',
                cellClassName: STATUS_CELL,
                headerClassName: STATUS_HEADER,
                render: (patient) => {
                    const phase = patient.internalCaseStatus
                        ? (catalog.meta(patient.internalCaseStatus)?.phase ?? null)
                        : null;
                    return (
                        <StatusPill
                            value={phase}
                            className="max-w-full"
                            colorOverride={phase ? phaseColorOverride(phase) : undefined}
                        />
                    );
                }
            },
            {
                key: 'internal-status',
                label: 'INTERNAL STATUS',
                width: '16%',
                cellClassName: STATUS_CELL,
                headerClassName: STATUS_HEADER,
                render: (patient) => (
                    <StatusPill
                        value={
                            patient.internalCaseStatus
                                ? catalog.display(patient.internalCaseStatus)
                                : null
                        }
                        className="max-w-full"
                    />
                )
            },
            {
                key: 'external-status',
                label: 'EXTERNAL STATUS',
                width: '16%',
                cellClassName: STATUS_CELL,
                headerClassName: STATUS_HEADER,
                render: (patient) => (
                    <StatusPill value={patient.caseStatus} className="max-w-full" />
                )
            },
            {
                key: 'action-required',
                label: 'ACTION REQUIRED',
                width: '18%',
                cellClassName: STATUS_CELL,
                headerClassName: STATUS_HEADER,
                render: (patient) => (
                    <StatusPill value={patient.actionRequired} className="max-w-full" />
                )
            },
            {
                key: 'view',
                // Named for the sr-only a11y header; headerNode keeps it blank visibly.
                label: 'Actions',
                headerNode: null,
                width: '8%',
                align: 'right',
                cellClassName: 'px-6 py-0',
                headerClassName: STATUS_HEADER,
                render: (patient) => (
                    <button
                        onClick={() => handleOpenFiles(patient)}
                        className="inline-flex items-center justify-center px-3 py-2 h-8 border border-gray-300 rounded-lg font-semibold text-sm text-gray-800 hover:bg-gray-100 transition-colors"
                    >
                        View
                    </button>
                )
            }
        ],
        [handleOpenFiles, catalog]
    );

    const hasActiveFilters =
        debouncedSearch.length > 0 ||
        table.filters.practice.length > 0 ||
        table.filters.caseStatus.length > 0 ||
        table.filters.macro.length > 0 ||
        table.filters.actionRequired.length > 0;

    // In-card bars above the column headers: background sync + result count.
    const cardHeader = (
        <>
            {isFetching && !isLoading && (
                <div className="px-6 py-2.5 border-b border-gray-200 bg-gradient-to-r from-blue-50/50 to-white flex items-center gap-2.5">
                    <div className="animate-spin h-3.5 w-3.5 border-2 border-blue-400 border-t-transparent rounded-full" />
                    <p className="text-sm text-gray-600 font-medium">Syncing latest data...</p>
                </div>
            )}
            {hasActiveFilters && (
                <div className="px-6 py-3 border-b border-gray-200 bg-gray-50">
                    <p className="text-sm text-gray-600">
                        {meta.total === 0
                            ? `No patients found${debouncedSearch ? ` matching "${debouncedSearch}"` : ''}`
                            : `Found ${meta.total} patient${meta.total === 1 ? '' : 's'}${debouncedSearch ? ` matching "${debouncedSearch}"` : ''}`}
                    </p>
                </div>
            )}
        </>
    );

    const emptyState = isError ? (
        <div className="flex flex-col items-center gap-3">
            <p className="text-red-500 font-medium">Failed to load patients</p>
            <button
                onClick={() => refresh()}
                className="text-xs text-gray-600 bg-gray-100 hover:bg-gray-200 rounded-lg px-3 py-1.5 transition-colors"
            >
                Retry
            </button>
        </div>
    ) : (
        <span>
            {hasActiveFilters ? 'No patients found matching your filters.' : 'No patients found.'}
        </span>
    );

    return (
        <div className="flex flex-col h-full w-full">
            <div className="flex flex-wrap items-center justify-between gap-y-3 w-full px-8 mt-6">
                <div className="flex items-center gap-4">
                    <div className="flex items-center w-[340px] h-11 bg-white border border-gray-200 rounded-xl px-4 flex-shrink-0 min-w-0">
                        <MagnifyingGlassIcon className="w-5 h-5 text-gray-400 mr-2 flex-shrink-0" />
                        <input
                            type="text"
                            value={searchTerm}
                            onChange={(e) => setSearchTerm(e.target.value)}
                            placeholder="Search by first or last name"
                            className="w-full min-w-0 bg-transparent outline-none text-gray-700 placeholder-gray-400 text-sm"
                        />
                        {searchTerm && (
                            <button
                                onClick={() => setSearchTerm('')}
                                className="ml-2 flex-shrink-0 text-gray-400 hover:text-gray-600 transition-colors"
                                aria-label="Clear search"
                            >
                                <XMarkIcon className="w-4 h-4" />
                            </button>
                        )}
                    </div>
                </div>
                <div className="flex items-center gap-4">
                    <FilterDropdown
                        title="Practice"
                        options={practiceOptions}
                        selectedOptions={table.filters.practice}
                        onChange={(v) => table.setFilter('practice', v)}
                    />
                    <FilterDropdown
                        title="Action Required"
                        options={filterOptions.actionsRequired}
                        selectedOptions={table.filters.actionRequired}
                        onChange={(v) => table.setFilter('actionRequired', v)}
                    />
                    <MoreFiltersMenu
                        categories={moreCategories}
                        allCategories={moreCategories}
                        hiddenActiveCount={moreActiveCount}
                    />
                </div>
            </div>

            <div className="flex-1 min-h-0 flex flex-col w-full">
                <div className="px-8 py-4 w-full h-full flex flex-col" ref={viewport.tableCardRef}>
                    <DataTable
                        label="Patients"
                        columns={columns}
                        rows={patients}
                        getRowKey={(patient) => patient.id}
                        cardHeader={cardHeader}
                        sort={{ by: table.sort, order: table.order, onSort: table.toggleSort }}
                        mode={table.mode}
                        onModeChange={table.setMode}
                        onLoadMore={handleLoadMore}
                        hasMore={hasNextPage}
                        isLoadingMore={isFetchingNextPage}
                        pagination={
                            meta.total > 0
                                ? {
                                      page: table.page,
                                      totalPages: meta.totalPages,
                                      total: meta.total,
                                      onPage: setPage
                                  }
                                : undefined
                        }
                        rowClassName={() => 'text-sm text-gray-700 hover:bg-gray-50'}
                        isLoading={isLoading}
                        emptyState={emptyState}
                        emptyStateCellClassName="px-6 py-8 text-center text-gray-500 text-sm"
                        refs={{
                            head: viewport.tableHeaderRef,
                            body: viewport.tableBodyRef,
                            pagination: viewport.paginationRef
                        }}
                    />
                </div>
            </div>
        </div>
    );
};

export { Patients };
